import { createContext, useState, useEffect, useCallback, type ReactNode } from 'react';
import { membershipService } from '../services/membershipService';
import type { MembershipInfo, MembershipTier } from '../services/membershipService';
import { useAuth } from '../hooks/useAuth';
import { logger } from '../utils/logger';

interface MembershipContextType {
  membershipInfo: MembershipInfo | null;
  membershipTiers: MembershipTier[];
  isLoading: boolean;
  error: string | null;
  refreshMembershipInfo: () => Promise<void>;
  getPointsToNextTier: () => number;
  getProgressPercentage: () => number;
}

const MembershipContext = createContext<MembershipContextType | undefined>(undefined);

export default MembershipContext;
export { MembershipContext };

interface MembershipProviderProps {
  children: ReactNode;
}

export function MembershipProvider({ children }: MembershipProviderProps) {
  const [membershipInfo, setMembershipInfo] = useState<MembershipInfo | null>(null);
  const [membershipTiers, setMembershipTiers] = useState<MembershipTier[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { isAuthenticated, user } = useAuth();

  const fetchMembershipInfo = useCallback(async () => {
    // Clear previous state when not authenticated
    if (!isAuthenticated || !user) {
      setMembershipInfo(null);
      setMembershipTiers([]);
      setError(null);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      logger.log('Fetching membership info for user:', user.id);

      // Fetch membership info once, reuse centralized profile among service calls
      const membershipResponse = await membershipService.getMembershipInfo();

      // Handle membership info response
      if (membershipResponse?.success && membershipResponse?.data) {
        setMembershipInfo(membershipResponse.data);
        logger.log('✅ Membership info successfully loaded');

        // Derive tiers from membershipResponse to avoid second profile fetch
        const currentTier = membershipResponse.data.currentTier;
        if (currentTier) {
          const tiers = [currentTier];
          setMembershipTiers(tiers);
        } else {
          setMembershipTiers([]);
        }
      } else {
        logger.warn('⚠️ No membership data found in response');
        setMembershipInfo(null);
        setMembershipTiers([]);
        if (membershipResponse?.error) {
          setError(membershipResponse.error);
        }
      }
    } catch (err: unknown) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to load membership info';
      setError(errorMsg);
      logger.error('❌ fetchMembershipInfo error:', err);
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated, user]);

  // Calculate points needed to reach next tier
  const getPointsToNextTier = useCallback(
    (currentPoints?: number): number => {
      if (!membershipTiers || membershipTiers.length === 0) {
        return 0;
      }

      const points = currentPoints ?? membershipInfo?.pointsBalance ?? 0;
      return membershipService.getPointsToNextTier(points, membershipTiers);
    },
    [membershipTiers, membershipInfo?.pointsBalance],
  );

  // Calculate progress percentage to next tier
  const getProgressPercentage = useCallback(
    (currentPoints?: number): number => {
      if (!membershipTiers || membershipTiers.length === 0) {
        return 0;
      }

      const points = currentPoints ?? membershipInfo?.pointsBalance ?? 0;
      return membershipService.getProgressPercentage(points, membershipTiers);
    },
    [membershipTiers, membershipInfo?.pointsBalance],
  );

  // Auto-fetch membership info when user authentication changes
  useEffect(() => {
    fetchMembershipInfo();
  }, [fetchMembershipInfo]);

  // Listen for role switch events to refresh membership data
  useEffect(() => {
    const handleRoleSwitch = (event: CustomEvent) => {
      logger.log('🔄 Role switch detected, refreshing membership data...', event.detail);
      fetchMembershipInfo();
    };

    // Listen for the custom roleSwitch event dispatched from AuthProvider
    window.addEventListener('roleSwitch', handleRoleSwitch as EventListener);

    return () => {
      window.removeEventListener('roleSwitch', handleRoleSwitch as EventListener);
    };
  }, [fetchMembershipInfo]);

  const contextValue: MembershipContextType = {
    membershipInfo,
    membershipTiers,
    isLoading,
    error,
    refreshMembershipInfo: fetchMembershipInfo,
    getPointsToNextTier,
    getProgressPercentage,
  };

  return <MembershipContext.Provider value={contextValue}>{children}</MembershipContext.Provider>;
}
